/**
 * @fileoverview Defines React components for rendering various icons, utilizing the `IconProps` interface. Exports icon components such as `GoogleIcon`, `GitHubIcon`, `BookIcon`, `SearchIcon`, and others as SVG elements. The `IconProps` interface specifies the properties for these icon components.
 */

import React from 'react'

interface IconProps {
  className?: string
  size?: number
}

export function GoogleIcon({ className = '', size = 20 }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={className}
      fill="currentColor"
    >
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      ></path>
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      ></path>
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      ></path>
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      ></path>
    </svg>
  )
}

export function GitHubIcon({ className = '', size = 24 }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 11.992188 1.9902344 C 6.4801941 1.9902344 1.9882813 6.4791756 1.9882812 11.992188 C 1.9882814 16.670788 5.2150518 20.612757 9.5683594 21.707031 L 10.501953 21.941406 L 10.501953 16.03125 C 10.501953 15.767012 10.564552 15.535239 10.679688 15.322266 L 11.121094 14.505859 L 10.228516 14.246094 C 8.527472 13.749956 7.4882814 12.622149 7.4882812 11.498047 C 7.4882812 10.851407 7.7988082 10.244158 8.3964844 9.7128906 L 8.7148438 9.4316406 L 8.6367188 9.0136719 C 8.5757074 8.6928433 8.6085356 8.3442353 8.5878906 8.0117188 C 8.9520986 8.1975921 9.3431546 8.3235101 9.6757812 8.5839844 L 9.9667969 8.8105469 L 10.324219 8.7207031 C 10.845004 8.5880074 11.411163 8.5039063 11.992188 8.5039062 C 12.573211 8.5039062 13.123069 8.5873372 13.646484 8.7207031 L 14.003906 8.8105469 L 14.292969 8.5839844 C 14.629257 8.3211017 15.02901 8.1948297 15.398438 8.0078125 C 15.377868 8.3418517 15.408896 8.6916178 15.347656 9.0136719 L 15.269531 9.4316406 L 15.585938 9.7128906 C 16.183613 10.244158 16.494141 10.851407 16.494141 11.498047 C 16.494141 12.622149 15.457489 13.748748 13.759766 14.232422 L 12.859375 14.488281 L 13.306641 15.310547 C 13.42431 15.52752 13.494141 15.783851 13.494141 16.03125 L 13.494141 21.925781 L 14.425781 21.693359 C 18.768394 20.611223 21.994141 16.669868 21.994141 11.992188 C 21.994141 6.4792735 17.505101 1.9902344 11.992188 1.9902344 z M 11.992188 3.4902344 C 16.693274 3.4902344 20.494141 7.2911005 20.494141 11.992188 C 20.494141 15.618342 18.175006 18.612961 14.994141 19.833984 L 14.994141 16.03125 C 14.994141 15.766348 14.871984 15.549703 14.806641 15.306641 C 16.587974 14.575939 17.994141 13.260122 17.994141 11.498047 C 17.994141 10.502019 17.514024 9.6317313 16.841797 8.9199219 C 16.950151 8.1920361 17.014888 7.4528484 16.953125 6.6914062 L 16.875 5.7382812 L 15.96875 6.0410156 C 15.184837 6.3023201 14.429229 6.7183803 13.707031 7.2246094 C 13.165799 7.1053503 12.605964 7.0039063 11.992188 7.0039062 C 11.37836 7.0039062 10.810771 7.1050737 10.265625 7.2246094 C 9.5436695 6.7178866 8.7902699 6.3039202 8.0195312 6.0429688 L 7.109375 5.734375 L 7.03125 6.6933594 C 6.969511 7.4554166 7.03427 8.1923467 7.1425781 8.9199219 C 6.4703505 9.6317313 5.9882813 10.502019 5.9882812 11.498047 C 5.9882812 13.263108 7.3982039 14.581716 9.1816406 15.320312 C 9.1211496 15.559205 9.0019531 15.777287 9.0019531 16.03125 L 9.0019531 16.496094 L 8.2460938 16.496094 C 7.9909147 16.496094 7.8304889 16.412374 7.5976562 16.199219 C 7.3648238 15.986066 7.1151954 15.647063 6.8261719 15.292969 L 6.3535156 14.710938 L 5.1914062 15.660156 L 5.6640625 16.242188 C 5.908039 16.541093 6.186817 16.94109 6.5839844 17.304688 C 6.9811518 17.668284 7.5542727 17.996094 8.2460938 17.996094 L 9.0019531 17.996094 L 9.0019531 19.84375 C 5.8102212 18.613484 3.4882812 15.618953 3.4882812 11.992188 C 3.4882812 7.2911984 7.2921799 3.4902344 11.992188 3.4902344 z" />
    </svg>
  )
}

export function GitHubFilledIcon({ className = '', size = 24 }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M1.993,11.993c0-5.52,4.48-10,10-10	s10,4.48,10,10c0,4.48-2.947,8.28-7,9.547v-5.507c0-0.133-0.013-0.253-0.027-0.386l-0.04-0.227c1.733-0.733,3.067-2.146,3.067-3.92	c0-1.013-0.44-1.906-1.107-2.613l0.027-0.227c0.08-0.64,0.093-1.294,0.04-1.973L16.886,5.74l-0.92,0.293	c-0.68,0.227-1.333,0.534-1.973,0.934l-0.32,0.227l-0.133-0.04c-0.493-0.094-1.013-0.147-1.547-0.147	c-0.547,0-1.067,0.053-1.56,0.147l-0.133,0.04l-0.32-0.227c-0.627-0.4-1.293-0.706-1.973-0.934L7.099,5.74l-0.08,0.947	c-0.053,0.68-0.04,1.333,0.04,1.973l0.04,0.227c-0.68,0.707-1.107,1.6-1.107,2.613c0,1.787,1.32,3.187,3.067,3.92l-0.04,0.227	C9.006,15.78,8.993,15.9,8.993,16.033V16.5H8.246c-0.12,0-0.227-0.027-0.32-0.066c-0.107-0.053-0.213-0.12-0.333-0.24	c-0.253-0.227-0.507-0.587-0.773-0.906L6.353,14.7l-1.16,0.947l0.467,0.587c0.12,0.16,0.267,0.333,0.413,0.52	c0.147,0.174,0.32,0.374,0.507,0.547c0.2,0.187,0.44,0.36,0.72,0.493c0.28,0.12,0.587,0.2,0.947,0.2h0.747v3.547	C4.939,20.273,1.993,16.473,1.993,11.993z" />
    </svg>
  )
}

export const BookIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 7.9726562 2.0058594 L 7.9726562 2.7558594 L 7.9726562 17.990234 L 20.996094 17.990234 L 20.996094 2.0058594 L 7.9726562 2.0058594 z M 9.4726562 3.5058594 L 12 3.5058594 L 12 9 L 13.5 8 L 15 9 L 15 3.5058594 L 19.496094 3.5058594 L 19.496094 16.490234 L 9.4726562 16.490234 L 9.4726562 3.5058594 z M 5.0078125 5.9785156 L 5.0078125 6.7285156 L 5.0078125 21.003906 L 17.251953 21.003906 L 18.001953 21.003906 L 18.001953 19.503906 L 17.251953 19.503906 L 6.5078125 19.503906 L 6.5078125 6.7285156 L 6.5078125 5.9785156 L 5.0078125 5.9785156 z" />
    </svg>
  )
}

export const InstallMCPIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 11.25 1 L 11.25 12.4375 L 9.3359375 10.525391 L 8.8066406 9.9960938 L 7.7460938 11.056641 L 8.2753906 11.585938 L 12 15.310547 L 15.724609 11.585938 L 16.253906 11.056641 L 15.193359 9.9960938 L 14.664062 10.525391 L 12.75 12.4375 L 12.75 1 L 11.25 1 z M 4 2 L 4 22 L 20 22 L 20 2 L 14.5 2 L 14.5 3.5 L 18.5 3.5 L 18.5 20.5 L 16.5 20.5 L 16.5 19 L 15 19 L 15 20.5 L 13.5 20.5 L 13.5 19 L 12 19 L 12 20.5 L 5.5 20.5 L 5.5 3.5 L 9.5 3.5 L 9.5 2 L 4 2 z" />
    </svg>
  )
}

export const SearchIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M 10 2 C 5.590603 2 2 5.5906063 2 10 C 2 14.409394 5.590603 18 10 18 C 11.929744 18 13.635779 17.22512 15.019531 16.082031 L 20.380859 21.441406 L 20.912109 21.972656 L 21.972656 20.912109 L 21.441406 20.380859 L 16.082031 15.019531 C 17.225119 13.635778 18 11.929742 18 10 C 18 5.5906063 14.409397 2 10 2 z M 10 3.5 C 13.598737 3.5 16.5 6.401265 16.5 10 C 16.5 13.598735 13.598737 16.5 10 16.5 C 6.4012627 16.5 3.5 13.598735 3.5 10 C 3.5 6.401265 6.4012627 3.5 10 3.5 z"></path>
    </svg>
  )
}

export const KeyIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 15.5 2 C 11.918773 2 9 4.9187733 9 8.5 C 9 8.9708312 9.100184 9.4102262 9.1953125 9.8496094 L 2 17.044922 L 2 22 L 6.9550781 22 L 8 20.955078 L 8 19 L 10 19 L 10 17 L 11.955078 17 L 14.150391 14.804688 C 14.59012 14.899919 15.030315 15 15.5 15 C 19.081227 15 22 12.081227 22 8.5 C 22 4.9187733 19.081227 2 15.5 2 z M 15.5 3.5 C 18.270773 3.5 20.5 5.7292267 20.5 8.5 C 20.5 11.270773 18.270773 13.5 15.5 13.5 C 15.0116 13.5 14.540913 13.42686 14.091797 13.294922 L 13.664062 13.169922 L 11.333984 15.5 L 8.5 15.5 L 8.5 17.5 L 6.5 17.5 L 6.5 20.333984 L 6.3339844 20.5 L 3.5 20.5 L 3.5 17.666016 L 10.830078 10.335938 L 10.705078 9.9101562 C 10.573527 9.4593732 10.5 8.9875115 10.5 8.5 C 10.5 5.7292267 12.729227 3.5 15.5 3.5 z M 16.5 6 A 1.5 1.5 0 0 0 16.5 9 A 1.5 1.5 0 0 0 16.5 6 z" />
    </svg>
  )
}

export const GraphIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 16.5 3 L 16.5 3.75 L 16.5 21 L 22 21 L 22 3 L 16.5 3 z M 18 4.5 L 20.5 4.5 L 20.5 19.5 L 18 19.5 L 18 4.5 z M 2.5 8 L 2.5 8.75 L 2.5 21 L 8 21 L 8 8 L 2.5 8 z M 4 9.5 L 6.5 9.5 L 6.5 19.5 L 4 19.5 L 4 9.5 z M 9.5 13 L 9.5 13.75 L 9.5 21 L 15 21 L 15 13 L 9.5 13 z M 11 14.5 L 13.5 14.5 L 13.5 19.5 L 11 19.5 L 11 14.5 z" />
    </svg>
  )
}

export const AccountIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 12 2 C 9.7997075 2 8 3.7997075 8 6 L 8 7 C 8 9.2002925 9.7997075 11 12 11 C 14.200292 11 16 9.2002925 16 7 L 16 6 C 16 3.7997075 14.200292 2 12 2 z M 12 3.5 C 13.389708 3.5 14.5 4.6102925 14.5 6 L 14.5 7 C 14.5 8.3897075 13.389708 9.5 12 9.5 C 10.610292 9.5 9.5 8.3897075 9.5 7 L 9.5 6 C 9.5 4.6102925 10.610292 3.5 12 3.5 z M 8.7226562 13 L 3 17.908203 L 3 21 L 21 21 L 21 17.908203 L 15.277344 13 L 15 13 L 14.328125 13 L 14.109375 13.359375 C 14.109375 13.359375 13.426281 14.5 12 14.5 C 10.573719 14.5 9.890625 13.359375 9.890625 13.359375 L 9.671875 13 L 8.7226562 13 z M 9.0878906 14.662109 C 9.5477887 15.202079 10.385414 16 12 16 C 13.614586 16 14.452211 15.202079 14.912109 14.662109 L 19.5 18.597656 L 19.5 19.5 L 4.5 19.5 L 4.5 18.597656 L 9.0878906 14.662109 z" />
    </svg>
  )
}

export const DashboardIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <rect x="3" y="3" width="7" height="9"></rect>
      <rect x="14" y="3" width="7" height="5"></rect>
      <rect x="14" y="12" width="7" height="9"></rect>
      <rect x="3" y="16" width="7" height="5"></rect>
    </svg>
  )
}

export const CloseIcon = ({ className = '', size = 16 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 3.7441406 2.6835938 L 2.6835938 3.7441406 L 3.2148438 4.2753906 L 10.939453 12 L 3.2246094 19.714844 L 2.6933594 20.246094 L 3.7539062 21.306641 L 4.2851562 20.775391 L 12 13.060547 L 19.716797 20.779297 L 20.248047 21.308594 L 21.308594 20.248047 L 20.779297 19.716797 L 13.060547 12 L 20.785156 4.2753906 L 21.314453 3.7460938 L 20.253906 2.6855469 L 19.724609 3.2148438 L 12 10.939453 L 4.2753906 3.2148438 L 3.7441406 2.6835938 z" />
    </svg>
  )
}

export const RightArrowIcon = ({ className = '', size = 16 }: IconProps) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 12h18m-9-9l9 9-9 9" />
    </svg>
  )
}

export const CopyIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ verticalAlign: 'middle', marginTop: '1px' }}
      role="img"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  )
}

export const CursorIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      height={size}
      style={{ flex: 'none', lineHeight: '1' }}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <title>Cursor</title>
      <path
        d="M11.925 24l10.425-6-10.425-6L1.5 18l10.425 6z"
        fill="currentColor"
        opacity="0.6"
      ></path>
      <path d="M22.35 18V6L11.925 0v12l10.425 6z" fill="currentColor" opacity="0.2"></path>
      <path d="M11.925 0L1.5 6v12l10.425-6V0z" fill="currentColor" opacity="0.4"></path>
      <path d="M22.35 6L11.925 24V12L22.35 6z" fill="currentColor" opacity="0.3"></path>
      <path d="M22.35 6l-10.425 6L1.5 6h20.85z" fill="currentColor"></path>
    </svg>
  )
}

export const PDFIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
    >
      <path d="M 14.738281 1.9902344 A 0.750075 0.750075 0 0 0 14.638672 2 L 4 2 L 4 12.5 L 5.5 12.5 L 5.5 3.5 L 14 3.5 L 14 8 L 18.5 8 L 18.5 12.5 L 20 12.5 L 20 7.3730469 A 0.750075 0.750075 0 0 0 20 7.1269531 L 20 6.4394531 L 15.560547 2 L 14.871094 2 A 0.750075 0.750075 0 0 0 14.738281 1.9902344 z M 15.5 4.0605469 L 17.939453 6.5 L 15.5 6.5 L 15.5 4.0605469 z M 3.4765625 14 L 3.4765625 22 L 4.9765625 22 L 4.9765625 20.007812 L 6.46875 20.007812 C 8.0180133 20.007812 9.3222656 18.781036 9.3222656 17.246094 L 9.3222656 16.761719 C 9.3222656 15.227394 8.0184436 14 6.46875 14 L 3.4765625 14 z M 10 14 L 10 22 L 13.042969 22 C 14.667219 22 16 20.668505 16 19.044922 L 16 16.957031 C 16 15.332781 14.667219 14 13.042969 14 L 10 14 z M 17 14 L 17 22 L 18.5 22 L 18.5 18.5 L 21 18.5 L 21 17 L 18.5 17 L 18.5 15.5 L 22 15.5 L 22 14 L 17 14 z M 4.9765625 15.5 L 6.46875 15.5 C 7.2430564 15.5 7.8222656 16.076043 7.8222656 16.761719 L 7.8222656 17.246094 C 7.8222656 17.933151 7.2434867 18.507813 6.46875 18.507812 L 4.9765625 18.507812 L 4.9765625 15.5 z M 11.5 15.5 L 13.042969 15.5 C 13.856719 15.5 14.5 16.143281 14.5 16.957031 L 14.5 19.044922 C 14.5 19.857339 13.856719 20.5 13.042969 20.5 L 11.5 20.5 L 11.5 15.5 z" />
    </svg>
  )
}

export const GearIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
    >
      <path d="M 9.8378906 1 L 9.5351562 3.4199219 C 8.1077365 3.8320286 6.843343 4.5755392 5.8046875 5.578125 L 3.5546875 4.6269531 L 1.3925781 8.3730469 L 3.3339844 9.8417969 C 3.1595527 10.540096 3 11.248018 3 12 C 3 12.751982 3.1595527 13.459904 3.3339844 14.158203 L 1.3925781 15.626953 L 3.5546875 19.373047 L 5.8046875 18.421875 C 6.843343 19.424461 8.1077364 20.167971 9.5351562 20.580078 L 9.8378906 23 L 14.162109 23 L 14.464844 20.580078 C 15.892264 20.167971 17.156658 19.424461 18.195312 18.421875 L 20.445312 19.373047 L 22.607422 15.626953 L 20.666016 14.158203 C 20.840387 13.459834 21 12.751982 21 12 C 21 11.248018 20.840447 10.540096 20.666016 9.8417969 L 22.607422 8.3730469 L 20.445312 4.6269531 L 18.195312 5.578125 C 17.156657 4.5755392 15.892264 3.8320286 14.464844 3.4199219 L 14.162109 1 L 9.8378906 1 z M 11.162109 2.5 L 12.837891 2.5 L 13.095703 4.5644531 L 13.613281 4.6777344 C 15.148094 5.0146665 16.503089 5.8234956 17.529297 6.9453125 L 17.886719 7.3359375 L 19.808594 6.5253906 L 20.646484 7.9765625 L 18.990234 9.2304688 L 19.150391 9.734375 C 19.376841 10.449194 19.5 11.209251 19.5 12 C 19.5 12.790749 19.377004 13.54938 19.150391 14.265625 L 18.990234 14.769531 L 19.412109 15.089844 L 20.646484 16.023438 L 19.808594 17.474609 L 17.886719 16.664062 L 17.529297 17.054688 C 16.503089 18.176503 15.148094 18.985333 13.613281 19.322266 L 13.095703 19.435547 L 12.837891 21.5 L 11.162109 21.5 L 10.904297 19.435547 L 10.386719 19.322266 C 8.8519063 18.985333 7.4969106 18.176504 6.4707031 17.054688 L 6.1132812 16.664062 L 4.1914062 17.474609 L 3.3535156 16.023438 L 5.0097656 14.769531 L 4.8496094 14.265625 C 4.623159 13.550806 4.5 12.790749 4.5 12 C 4.5 11.209251 4.623159 10.449194 4.8496094 9.734375 L 5.0097656 9.2304688 L 3.3535156 7.9765625 L 4.1914062 6.5253906 L 6.1132812 7.3359375 L 6.4707031 6.9453125 C 7.4969106 5.8234956 8.8519063 5.0146665 10.386719 4.6777344 L 10.904297 4.5644531 L 11.162109 2.5 z M 12 8 C 10.729167 8 9.6810741 8.5035667 9.0019531 9.2675781 C 8.3228321 10.03159 8 11.020833 8 12 C 8 12.979167 8.3228321 13.96841 9.0019531 14.732422 C 9.6810741 15.496433 10.729167 16 12 16 C 13.270833 16 14.318926 15.496433 14.998047 14.732422 C 15.677168 13.96841 16 12.979167 16 12 C 16 11.020833 15.677168 10.03159 14.998047 9.2675781 C 14.318926 8.5035667 13.270833 8 12 8 z M 12 9.5 C 12.895833 9.5 13.472741 9.8089332 13.876953 10.263672 C 14.281165 10.71841 14.5 11.354167 14.5 12 C 14.5 12.645833 14.281165 13.28159 13.876953 13.736328 C 13.472741 14.191067 12.895833 14.5 12 14.5 C 11.104167 14.5 10.527259 14.191067 10.123047 13.736328 C 9.7188349 13.28159 9.5 12.645833 9.5 12 C 9.5 11.354167 9.7188349 10.71841 10.123047 10.263672 C 10.527259 9.8089332 11.104167 9.5 12 9.5 z" />
    </svg>
  )
}

export const WebIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
    >
      <path d="M 12 2 C 8.4765025 2 5.3681187 3.8309916 3.5878906 6.59375 L 3.1816406 7.2246094 L 4.4414062 8.0371094 L 4.8476562 7.40625 C 6.2307592 5.2597951 8.5684974 3.8360945 11.25 3.5957031 L 11.25 7 L 11.25 7.75 L 12.75 7.75 L 12.75 7 L 12.75 3.5957031 C 15.431503 3.8360945 17.769241 5.2597951 19.152344 7.40625 L 19.558594 8.0371094 L 20.818359 7.2246094 L 20.412109 6.59375 C 18.631881 3.8309916 15.523498 2 12 2 z M 2.9570312 8.9609375 L 1.9609375 9.0429688 L 2.0019531 9.5410156 L 2.5878906 16.574219 L 5 14.304688 L 7.4121094 16.574219 L 7.9980469 9.5410156 L 8.0390625 9.0429688 L 7.0429688 8.9609375 L 7.0019531 9.4589844 L 6.5878906 14.425781 L 5 12.929688 L 3.4121094 14.425781 L 2.9980469 9.4589844 L 2.9570312 8.9609375 z M 9.9570312 8.9609375 L 8.9609375 9.0429688 L 9.0019531 9.5410156 L 9.5859375 16.5625 L 12 14.333984 L 14.414062 16.5625 L 14.998047 9.5410156 L 15.039062 9.0429688 L 14.042969 8.9609375 L 14.001953 9.4589844 L 13.587891 14.4375 L 12 12.972656 L 10.412109 14.4375 L 9.9980469 9.4589844 L 9.9570312 8.9609375 z M 16.957031 8.9609375 L 15.960938 9.0429688 L 16.001953 9.5410156 L 16.587891 16.574219 L 19 14.304688 L 21.412109 16.574219 L 21.998047 9.5410156 L 22.039062 9.0429688 L 21.042969 8.9609375 L 21.001953 9.4589844 L 20.587891 14.425781 L 19 12.929688 L 17.412109 14.425781 L 16.998047 9.4589844 L 16.957031 8.9609375 z M 11.25 16.75 L 11.25 17.5 L 11.25 20.332031 C 8.9819023 20.121521 6.9319328 19.152254 5.5332031 17.513672 L 5.0449219 16.943359 L 3.9042969 17.916016 L 4.390625 18.486328 C 6.224284 20.634421 8.9590028 22 12 22 C 15.040997 22 17.775716 20.634421 19.609375 18.486328 L 20.095703 17.916016 L 18.955078 16.943359 L 18.466797 17.513672 C 17.068067 19.152254 15.018098 20.121521 12.75 20.332031 L 12.75 17.5 L 12.75 16.75 L 11.25 16.75 z" />
    </svg>
  )
}

export const BugIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
    >
      <path d="M 8.4023438 2.0644531 L 6.9941406 2.5820312 C 7.3452097 3.5374215 7.9838853 4.201283 8.5097656 4.6308594 C 8.2859568 4.8934398 8.0936346 5.1663421 7.9394531 5.4355469 C 7.6186104 5.9957464 7.4675911 6.4302932 7.375 6.78125 L 6.0136719 5.6015625 L 3.2949219 4.9453125 L 2.9433594 6.4023438 L 5.3066406 6.9746094 L 6.3496094 7.8769531 C 6.1766863 8.0017592 5.98995 8.1519871 5.7753906 8.3652344 C 5.6639836 8.4759601 5.5585042 8.6255897 5.4492188 8.7558594 L 2.9472656 9.25 L 0.83398438 10.705078 L 1.6855469 11.941406 L 3.5410156 10.662109 L 4.4394531 10.484375 C 4.1745592 11.19621 4 12.031499 4 13.015625 C 4 14.792254 4.5605442 16.261381 5.3652344 17.460938 L 5.2695312 17.382812 L 4.1464844 18.751953 L 1.9550781 19.814453 L 2.609375 21.164062 L 5.0976562 19.958984 L 6.2402344 18.564453 C 6.778926 19.148898 7.3608096 19.654783 7.9414062 20.066406 C 9.8697876 21.433562 11.796875 21.972656 11.796875 21.972656 L 12 22.029297 L 12.203125 21.972656 C 12.203125 21.972656 14.130212 21.433562 16.058594 20.066406 C 16.63919 19.654783 17.221074 19.148898 17.759766 18.564453 L 18.902344 19.958984 L 21.390625 21.164062 L 22.044922 19.814453 L 19.853516 18.751953 L 18.730469 17.382812 L 18.634766 17.460938 C 19.439456 16.261381 20 14.792254 20 13.015625 C 20 12.031499 19.825441 11.19621 19.560547 10.484375 L 20.458984 10.662109 L 22.314453 11.941406 L 23.166016 10.705078 L 21.052734 9.25 L 18.550781 8.7558594 C 18.441496 8.6255897 18.336016 8.4759601 18.224609 8.3652344 C 18.01005 8.1519871 17.823314 8.0017592 17.650391 7.8769531 L 18.693359 6.9746094 L 21.056641 6.4023438 L 20.705078 4.9453125 L 17.986328 5.6015625 L 16.625 6.78125 C 16.532409 6.4302932 16.38139 5.9957464 16.060547 5.4355469 C 15.906365 5.1663421 15.714043 4.8934398 15.490234 4.6308594 C 16.016115 4.201283 16.65479 3.5374215 17.005859 2.5820312 L 15.597656 2.0644531 C 15.329115 2.7952543 14.703377 3.3394212 14.308594 3.6328125 C 13.678014 3.2555293 12.916926 3 12 3 C 11.083074 3 10.321986 3.2555293 9.6914062 3.6328125 C 9.2966226 3.3394212 8.670885 2.7952543 8.4023438 2.0644531 z M 12 4.5 C 13.496044 4.5 14.251226 5.293718 14.759766 6.1816406 C 14.994966 6.592306 15.143864 6.9964053 15.242188 7.3222656 C 14.62898 7.6045331 13.491301 8.0292969 12 8.0292969 C 10.508699 8.0292969 9.3710199 7.6045331 8.7578125 7.3222656 C 8.856136 6.9964053 9.0050342 6.592306 9.2402344 6.1816406 C 9.7487739 5.293718 10.503956 4.5 12 4.5 z M 8.0019531 8.6230469 C 8.7294645 8.9699275 10.157625 9.5292969 12 9.5292969 C 13.842375 9.5292969 15.270536 8.9699275 15.998047 8.6230469 C 16.117964 8.6673734 16.588035 8.8532999 17.167969 9.4296875 C 17.826493 10.084182 18.5 11.145248 18.5 13.015625 C 18.5 15.817814 16.888025 17.638953 15.191406 18.841797 C 14.216963 19.532643 13.349575 19.92741 12.75 20.160156 L 12.75 12 L 11.25 12 L 11.25 20.160156 C 10.650425 19.92741 9.7830367 19.532643 8.8085938 18.841797 C 7.111975 17.638953 5.5 15.817814 5.5 13.015625 C 5.5 11.145248 6.1735102 10.084182 6.8320312 9.4296875 C 7.4119653 8.8532999 7.8820361 8.6673734 8.0019531 8.6230469 z" />
    </svg>
  )
}

export const ClaudeIcon = ({ className = '', size = 24 }: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      shapeRendering="optimizeQuality"
      baseProfile="basic"
    >
      <linearGradient
        id="Il_Yw9TpWsua81mVHsLWRa"
        x1="24"
        x2="24"
        y1="2.987"
        y2="45.013"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#d97757" />
        <stop offset="1" stopColor="#db5b32" />
      </linearGradient>
      <path
        fill="url(#Il_Yw9TpWsua81mVHsLWRa)"
        d="M11.239,30.934l8.264-4.637l0.139-0.403l-0.139-0.224h-0.403l-1.381-0.085l-4.722-0.128l-4.095-0.17l-3.968-0.213l-0.998-0.213	L3,23.628l0.096-0.615l0.839-0.564l1.203,0.105l2.657,0.182l3.988,0.275l2.893,0.17l4.285,0.445h0.681l0.096-0.275l-0.233-0.17	l-0.182-0.17l-4.127-2.796l-4.467-2.955l-2.34-1.702l-1.265-0.862l-0.638-0.808l-0.275-1.764l1.149-1.265l1.543,0.105l0.394,0.105	l1.563,1.203l3.338,2.584l4.359,3.21l0.638,0.53l0.255-0.182l0.031-0.128l-0.286-0.479l-2.371-4.285l-2.53-4.359L13.17,7.355	l-0.298-1.083c-0.105-0.445-0.182-0.82-0.182-1.276l1.307-1.775l0.723-0.233l1.744,0.233L17.2,3.858l1.083,2.479l1.756,3.902	l2.723,5.306l0.797,1.574l0.425,1.458l0.159,0.445h0.275v-0.255l0.224-2.989l0.414-3.67l0.403-4.722l0.139-1.33l0.658-1.594	L27.564,3.6l1.021,0.488l0.839,1.203l-0.116,0.777l-0.499,3.245l-0.978,5.082l-0.638,3.403h0.372l0.425-0.425l1.722-2.286	l2.893-3.616l1.276-1.435l1.489-1.585l0.956-0.754h1.807l1.33,1.977l-0.596,2.042l-1.86,2.36l-1.543,1.999l-2.212,2.978	l-1.381,2.382l0.128,0.19l0.329-0.031l4.997-1.064l2.7-0.488l3.222-0.553l1.458,0.681l0.159,0.692l-0.573,1.415l-3.446,0.851	l-4.041,0.808l-6.018,1.424l-0.074,0.054l0.085,0.105l2.711,0.255l1.16,0.062h2.839l5.287,0.394l1.381,0.913L45,28.26l-0.139,0.851	l-2.127,1.083l-2.87-0.681l-6.699-1.594l-2.297-0.573H30.55v0.19l1.914,1.872l3.508,3.168l4.393,4.084l0.224,1.01l-0.564,0.797	l-0.596-0.085l-3.86-2.904l-1.489-1.307l-3.372-2.839h-0.224v0.298l0.777,1.137l4.104,6.169l0.213,1.892l-0.298,0.615l-1.064,0.372	l-1.168-0.213l-2.402-3.372l-2.479-3.798l-1.999-3.403l-0.244,0.139l-1.18,12.709l-0.553,0.649l-1.276,0.488l-1.064-0.808	l-0.564-1.307l0.564-2.584l0.681-3.372l0.553-2.68l0.499-3.33l0.298-1.106l-0.02-0.074l-0.244,0.031l-2.51,3.446l-3.817,5.159	l-3.02,3.233l-0.723,0.286l-1.254-0.649l0.116-1.16l0.701-1.032l4.18-5.318l2.521-3.296l1.628-1.903l-0.011-0.275h-0.096	l-11.103,7.209l-1.977,0.255l-0.851-0.797l0.105-1.307l0.403-0.425l3.338-2.297l-0.011,0.011L11.239,30.934z"
        shapeRendering="optimizeQuality"
      />
    </svg>
  )
}
